import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { httpSignUpUser } from '../Hooks/user/user.post.hooks';
import { Link, useNavigate } from 'react-router-dom';
import Auth from '../Helpers/auth';
import AuthImage from '../Visuals/Images/Authentication.jpg';
import {
  AuthContainer,
  PictureSide,
  AuthenticationSide,
  AuthContainer2,
} from './AuthStyles.styles';
import HelmetHelper from '../Components/PageHelmet/HelmetHelper.component';

const PrimaryButton = React.lazy(
  () => import('../Components/Buttons/PrimaryButton/PrimaryButton.component')
);
const InputContainer = React.lazy(() => import('../Components/Form/Input'));
const PasswordInput = React.lazy(
  () => import('../Components/Form/PasswordInput')
);
const SignupOptions = React.lazy(() => import('./SignupOptions.component'));

const SignUpComponent = () => {
  const navigate = useNavigate();
  const [showPass, setShowPass] = useState(false);
  const [verifyShowPass, setVerifyShowPass] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formState, setFormState] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    verifyPassword: '',
  });

  // Mutation for signing up the user
  const mutation = useMutation({
    mutationFn: (newUser) => httpSignUpUser(newUser),
    onSuccess: async (data) => {
      try {
        const dataJ = await data.json();
        // console.log('Signup successful:', dataJ);

        // Save the token and set success message
        Auth.login(dataJ.token);
        localStorage.setItem('temp_id', dataJ.user[0]._id);
        setSuccess(
          'Your account has been created successfully! Redirecting...'
        );

        // Delay navigation to allow the user to see the success message
        setTimeout(() => {
          navigate('/user');
        }, 2000);
      } catch (err) {
        console.error('Error parsing signup response:', err);
        setError('An unexpected error occurred. Please try again.');
      }
    },
    onError: (error) => {
      console.error('Error signing up:', error);
      setError(
        error.response?.data?.message || 'Your account could not be created.'
      );
    },
  });

  // Handle form input changes
  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState({
      ...formState,
      [name]: value,
    });
    if (error) {
      setError('');
    }
  };

  // Handle form submission
  const handleFormSubmit = (e) => {
    e.preventDefault();

    // Basic validation
    if (formState.firstName.length < 2) {
      setError('First Name must be at least 2 characters long.');
      return;
    }
    if (formState.lastName.length < 3) {
      setError('Last Name must be at least 3 characters long.');
      return;
    }
    if (formState.email.length < 6 || !formState.email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    if (formState.password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }
    if (formState.password !== formState.verifyPassword) {
      setError('Passwords do not match.');
      return;
    }

    // Trigger the mutation
    mutation.mutate(formState);
  };

  return (
    <AuthContainer>
      <HelmetHelper title="Sign Up" description="Create your account" />
      <AuthContainer2>
        <PictureSide>
          <img src={AuthImage} loading="eager" alt="Yellow Helmets" />
        </PictureSide>
        <AuthenticationSide>
          <form onSubmit={handleFormSubmit}>
            <h2>Sign Up</h2>
            <div>
              {/* First Name */}
              <InputContainer
                label="First Name"
                value={formState.firstName}
                name="firstName"
                onChange={handleChange}
              />
              {/* Last Name */}
              <InputContainer
                label="Last Name"
                value={formState.lastName}
                name="lastName"
                onChange={handleChange}
              />
            </div>
            {/* Email */}
            <InputContainer
              label="Email"
              value={formState.email}
              name="email"
              onChange={handleChange}
            />
            {/* Password */}
            <PasswordInput
              label="Password"
              span="Must be at least 6 characters"
              handleChange={(e) => {
                e.preventDefault();
                setShowPass((prev) => !prev);
              }}
              showPass={showPass}
              value={formState.password}
              name="password"
              onChange={handleChange}
            />
            {/* Verify Password */}
            <PasswordInput
              label="Verify Password"
              handleChange={(e) => {
                e.preventDefault();
                setVerifyShowPass((prev) => !prev);
              }}
              value={formState.verifyPassword}
              onChange={handleChange}
              name="verifyPassword"
              showPass={verifyShowPass}
            />
            {/* Success and Error Messages */}
            {success && <p className="success">{success}</p>}
            {error && <p className="error">{error}</p>}
            <PrimaryButton span="Sign Up" />
            <span className="largeSpan" style={{ textAlign: 'center' }}>
              By signing up, you agree to our{' '}
              <Link style={{ fontSize: '0.7rem' }} to="/privacy-policy">
                Privacy Policy
              </Link>{' '}
              and{' '}
              <Link style={{ fontSize: '0.7rem' }} to="/terms-and-conditions">
                Terms & Conditions
              </Link>
            </span>
          </form>
          <SignupOptions />
        </AuthenticationSide>
      </AuthContainer2>
    </AuthContainer>
  );
};

export default SignUpComponent;
